import React, { Component } from 'react';
import _ from 'underscore';
import * as uuid from 'uuid';
import FieldItem from './FieldItem.jsx';
import CheckableField from './CheckableField.jsx';
import MultipleValueTextareaSwitch from './MultipleValueTextareaSwitch.jsx';

export default class Field extends Component {
  constructor(props) {
    super(props);

    this.state = this.parseInitialState(props);
    this.state.errors = [];
    if (props.errors) {
      Object.entries(props.errors).forEach(([errorIndex, message]) => {
        this.state.errors[errorIndex] = message;
      });
    }

    this.initialReference =
      typeof props.initialReference === 'undefined'
        ? uuid.v4()
        : props.initialReference;
    this.classes = ['TextFieldItem'];
    if (this.props.outside) {
      this.classes.push('outside');
    }
    this.updateIpFieldData = this.updateIpFieldData.bind(this);

    this.isTextareaType = (props.definitionTypes || [props.type]).find(
      (type) => {
        return ![
          'ip',
          'number',
          'boolean',
          'textarea',
          'mac',
          'date',
          'range',
          'dropdown',
        ].includes(type);
      },
    );
  }

  createItems(values) {
    if (!Array.isArray(values)) {
      values = [values];
    } else if (values.length === 0) {
      values = [''];
    }
    return values.map((v) => this.createItem(v));
  }

  createItem(value = '') {
    // replace falsy values with empty string, except for boolean value false
    if (!value && typeof value !== 'boolean' && typeof value !== 'number') {
      value = '';
    }
    return { value: value, id: uuid.v4() };
  }

  parseInitialState(props) {
    return {
      metadata: Array.isArray(props.metadata)
        ? props.metadata
        : [props.metadata],
      items: this.createItems(props.value),
      appendValuesForIp: this.hasAppendValues(props)
        ? props.ipFieldData.appendValues
        : undefined,
    };
  }

  updateAt(index, list, newValue) {
    let cpList = [...list];

    cpList[index] = newValue;
    return cpList;
  }

  removeAt(index, list) {
    let cpList = [...list];

    cpList.splice(index, 1);
    return cpList;
  }

  push(list, newValue) {
    return [...list, newValue];
  }

  setValue(index, value, metadata) {
    const item = this.state.items[index] || {};
    const newValue = this.escapeValue(value);

    // retrieve netmask (append field) from value if value empty or value includes slash
    if (
      this.hasAppendValues() &&
      (newValue === '' ||
        newValue.toString().includes('/') ||
        item.value.toString().includes('/'))
    ) {
      this.updateIpFieldData(index, {
        value: newValue.split('/')[1] || '',
        reference: '',
      });
    }

    item.value = newValue;
    this.broadCastState({
      items: this.updateAt(index, this.state.items, item),
      metadata: this.updateAt(index, this.state.metadata, metadata),
    });
  }

  hasAppendValues(props = this.props) {
    return (
      props.ipFieldData && props.ipFieldData.hasOwnProperty('appendValues') // eslint-disable-line no-prototype-builtins
    );
  }

  hashOnChange() {
    let { items, metadata } = this.state,
      value = items.map((item) => item.value);

    if (!this.props.multiple) {
      [value, metadata] = [value[0], metadata[0]];
    }
    this.props.onChange(this.props.index, { value, metadata });
  }

  broadCastState({ items, metadata, ...remainingState }) {
    if (this.isHash()) {
      this.setState({ items, metadata }, () => this.hashOnChange());
    } else {
      this.setState({ items: items, metadata: metadata, ...remainingState });
    }
  }

  findReference(referenceID) {
    return this.props.references.find(
      (reference) => reference.value == referenceID,
    );
  }

  updateIpFieldData(index, { value, reference }) {
    let updatedAppend = ({ values, references, ...remaining }) => ({
      values: this.updateAt(index, values, value),
      references: this.updateAt(index, references, reference),
      ...remaining,
    });

    this.setState(({ appendValuesForIp }) => ({
      appendValuesForIp: { ...updatedAppend(appendValuesForIp) },
    }));
  }

  removeValue(index) {
    let newState = {
      items: this.removeAt(index, this.state.items),
      metadata: this.removeAt(index, this.state.metadata),
      errors: this.removeAt(index, this.state.errors),
    };

    if (!newState.items.length && this.props.multiple) {
      newState.items.push({ value: null, id: uuid.v4() });
    }
    if (this.hasAppendValues()) {
      let newAppendValues = ({ values, references, ...remaining }) => ({
        ...remaining,
        values: this.removeAt(index, values),
        references: this.removeAt(index, references),
      });

      newState = {
        ...newState,
        appendValuesForIp: { ...newAppendValues(this.state.appendValuesForIp) },
      };
    }
    this.broadCastState(newState);
  }

  addValue() {
    let newState = {
      items: this.push(
        this.state.items.filter((item) => item.value !== null),
        this.createItem(),
      ),
      metadata: this.push(this.state.metadata, {}),
    };

    if (this.hasAppendValues()) {
      let newAppendValues = ({ values, references, ...remaining }) => ({
        ...remaining,
        values: this.push(values, ''),
        references: this.push(references, ''),
      });

      newState = {
        ...newState,
        appendValuesForIp: { ...newAppendValues(this.state.appendValuesForIp) },
      };
    }
    this.broadCastState(newState);
  }

  hasReference(metadata) {
    return (
      metadata && metadata.reference && !isNaN(parseInt(metadata.reference))
    );
  }

  // We assume that if someone is listening is a hash
  isHash() {
    return typeof this.props.onChange !== 'undefined';
  }

  showValue(value, index) {
    if (value === true || value === 'true') {
      return ['Enabled'];
    }
    if (value === false || value === 'false') {
      return ['Disabled'];
    }
    if (this.props.type === 'ip') {
      return this.showIpValue(value, index);
    }
    if (this.props.type == 'password') {
      return ['**********'];
    }

    if (this.hasReference(this.state.metadata[index])) {
      const reference =
        this.findReference(this.state.metadata[index].reference) || {};

      return [
        (reference.label || '') +
          (reference.customLabel ? ` (${reference.customLabel})` : ''),
      ];
    }

    return [this.unescapeValue(value)];
  }

  showIpValue(value, index) {
    let metadata = this.state.metadata[index] || {},
      displayValue = [];

    if (this.hasReference(metadata)) {
      const reference = this.findReference(metadata.reference);

      if (reference) {
        displayValue.push(
          reference.hasOwnProperty('templateName') // eslint-disable-line no-prototype-builtins
            ? reference.templateName
            : reference.value,
        );
      }
    } else {
      displayValue.push(value);
    }

    if (
      metadata.octet &&
      metadata.octetReplacement &&
      metadata.octet !== 'na'
    ) {
      displayValue.push(
        `Replace octet ${metadata.octet} with ${metadata.octetReplacement}`,
      );
    }

    if (
      this.state.appendValuesForIp &&
      this.state.appendValuesForIp.values[index] &&
      typeof value === 'string' &&
      value.indexOf(`/${this.state.appendValuesForIp.values[index]}`) === -1
    ) {
      displayValue.push(
        `Append ${this.state.appendValuesForIp.values[index]} to value`,
      );
    }

    return displayValue;
  }

  isFocused(index) {
    return index === this.state.items.length - 1;
  }

  indexClass(index) {
    if (index === 0) {
      return 'first';
    }
    if (index === this.state.items.length - 1) {
      return 'last';
    }
    return '';
  }

  hiddenIfEmpty(item) {
    if (item && item.value === null) {
      return 'hidden';
    }
  }

  mapAppendValuesForIp(index) {
    if (!this.hasAppendValues()) {
      return this.props.ipFieldData;
    }

    let { appendValues, ...commonIpData } = this.props.ipFieldData, // eslint-disable-line no-unused-vars
      getAppendItem = ({ values, references }) => ({
        value: values[index] || '',
        reference: references[index] || '',
      });

    return {
      ...commonIpData,
      onAppendChange: this.updateIpFieldData,
      appendValue: getAppendItem(this.state.appendValuesForIp),
    };
  }

  hasError() {
    return (
      (this.state.errors && this.state.errors.length > 0) ||
      (this.props.errors && Object.keys(this.props.errors).length > 0)
    );
  }

  findError(index) {
    let errorArray, errors;

    if (typeof this.state.errors !== 'undefined') {
      errors = this.state.errors;
    }
    if (
      typeof errors === 'undefined' &&
      typeof this.props.errors !== 'undefined'
    ) {
      errors = this.props.errors;
    }

    if (!this.props.multiple && errors) {
      errorArray = errors;
    } else if (this.props.multiple && errors) {
      // Filter the error messages that matches the current index
      errorArray = errors
        .filter((message) => message.indexOf(`${index}:`) === 0)
        .map((message) => message.replace(`${index}:`, ''));

      // Pushes errors which don't match any index to the last field
      if (index === this.state.items.length - 1) {
        errorArray = errorArray.concat(
          errors.filter((message) => !message.match(/^[0-9]+:.*$/g)),
        );
      }
    }

    return errorArray.length > 1 ? errorArray.join('; ') : _.first(errorArray);
  }

  escapeValue(value) {
    return typeof value === 'string' && value.startsWith('$')
      ? `$${value}`
      : value;
  }

  unescapeValue(value) {
    return typeof value === 'string' && value.startsWith('$$')
      ? value.replace('$', '')
      : value;
  }

  inheritedProps() {
    const defaultProps = [
      'addValueStrToInputName',
      'fixed',
      'id',
      'mask',
      'multiple',
      'outside',
      'placeholder',
      'propertyId',
      'readonly',
      'references',
      'referenceWarning',
      'required',
      'semantic',
      'type',
    ];
    let props = {};

    defaultProps.forEach((p) => (props[p] = this.props[p]));
    return props;
  }

  render() {
    const parentDisabled =
      typeof this.props.parentDisabled !== 'undefined'
        ? this.props.parentDisabled
        : false;
    const items = this.state.items;
    const displayTextarea =
      this.isTextareaType &&
      this.props.multiple &&
      !this.hasError() &&
      !this.props.readonly;

    return (
      <div className={`FieldItem ${this.props.semantic ? 'ui form' : ''}`}>
        <div className='FieldItemForm hide-disabled'>
          <MultipleValueTextareaSwitch
            display={displayTextarea}
            initialReference={this.initialReference}
            isHash={this.isHash()}
            metadata={this.state.metadata}
            multiple={this.props.multiple}
            name={this.props.name}
            references={this.props.references}
            value={this.state.items}
            onChange={(val) => this.broadCastState(val)}
          >
            <div className={`TextFieldItems ${this.hiddenIfEmpty(items[0])}`}>
              {' '}
              {items.map((item, index) => {
                if (this.props.readonly) {
                  return <pre key={item.id}>{item.value}</pre>;
                } else {
                  return (
                    <FieldItem
                      key={item.id}
                      ref={index}
                      className={`${this.indexClass(index)} ${this.hiddenIfEmpty(item)}`}
                      default={this.props.default}
                      error={this.findError(index)}
                      fieldmeta={this.props.fieldmeta}
                      focus={this.isFocused(index)}
                      getMetadataAlias={(attr) => this.getMetadataAlias(attr)}
                      getSufixName={(field) => this.getSufixName(field)}
                      index={index}
                      initialReferenceState={
                        this.isHash() ? this.initialReference : ''
                      }
                      ipFieldData={this.mapAppendValuesForIp(index)}
                      isHash={this.isHash()}
                      metadata={
                        this.state.metadata[index] !== undefined
                          ? this.state.metadata[index]
                          : null
                      }
                      name={this.props.name}
                      parentDisabled={parentDisabled}
                      readonly={this.props.readonly}
                      removeValue={this.removeValue.bind(this)}
                      setValue={(idx, val, md) => this.setValue(idx, val, md)}
                      supressFields={this.isHash()}
                      value={this.unescapeValue(item.value)}
                      {...this.inheritedProps()}
                    />
                  );
                }
              })}{' '}
            </div>
            <If
              condition={
                this.props.multiple && !parentDisabled && !this.props.readonly
              }
            >
              <div className='FieldItemAdd'>
                <a
                  className='ui primary button multiple-value-button multiple-value-button-add io-form-button'
                  onClick={() => this.addValue()}
                >
                  <i className='icon plus circle'></i> {this.props.label}
                </a>
              </div>
            </If>
          </MultipleValueTextareaSwitch>
        </div>
        <If condition={!this.props.checkableChannel}>
          <ul
            className={`FieldItemDisplay rawtext show-disabled disabled-${parentDisabled}`}
          >
            {items.map((item, index) => {
              return (
                <li key={index}>
                  <label
                    className='rawtext'
                    data-testid={'rawtext'}
                  >
                    {this.showValue(item.value, index).map(
                      (displayValue, _index) => {
                        return (
                          <span
                            key={_index}
                            className='FieldDisplayItem'
                          >
                            {displayValue}
                          </span>
                        );
                      },
                    )}
                  </label>
                </li>
              );
            })}{' '}
          </ul>
        </If>
        <If condition={this.props.checkableChannel}>
          <CheckableField
            channel={this.props.checkableChannel}
            checkableLink={this.props.checkableLink}
            metadata={this.props.metadata}
            objectIdentifier={this.props.objectIdentifier}
            propertyId={this.props.propertyId}
            value={items
              .map((item, index) => this.showValue(item.value, index).join(''))
              .join('')}
          />
        </If>
      </div>
    );
  }
}
